import React, {useState, useEffect, useRef} from 'react';
import { injectIntl } from 'react-intl';
import { getLayout, getPromotionMainColor, i18nIntl, parseFontName, isColorBright, RGB_2_HEX } from '../../lib/ep.js';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import ActionButton, { SecondaryButton } from '../builder/ActionButton';
import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar';
import Checkbox from '../builder/Checkbox.js';
import ColorPicker from '../builder/ColorPicker.js';
import DisabledArea from '../builder/DisabledArea.js';
import DynamicFields, { createField } from '../builder/DynamicFields.js';
import FloatingLayer from '../builder/FloatingLayer';
import { AutoImageUploader, AutoPostImageUploader } from '../builder/ImageUploader.js';
import Input from '../builder/Input';
import Message from '../builder/Message.js';
import Model from '../builder/Model';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import Range from '../builder/Range.js';
import RichEditor from '../builder/RichEditor';
import Select from '../builder/Select';
import Spinner from '../builder/Spinner';
import Tooltip from '../builder/Tooltip';

import BulkFromFile from '../utils/BulkFromFile.js';

import { GameContainerWrapper } from '../front/GameContainer.js';
import GuessWordClient from '../front/GuessWordClient.js';
import Layout from '../front/Layout';

import transferPlaceholder from '../../hoc/transferPlaceholder';
import MultiOptionsCreator from './MultiOptionsCreator.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import GameInstructionsBuilder from './GameInstructionsBuilder.js';
import GameScoreBuilder from './GameScoreBuilder.js';
import GameSoundtrackFields from '../builder/GameSoundtrackFields.js';

const RichEditorPlaceHolder = injectIntl(transferPlaceholder(RichEditor));

let guessword_min = [{ k:'', v:"Choose the min crushes to win" }];
for(var i = 5; i <= 50 ; i++) {
	guessword_min.push({ k:i, v:i });
}

const total_elements = 500;
const recommended_time_per_word = 30;	// sec

const GuessWordField = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [uploading, setUploading] = useState(false);

	const auto_img_hooks = (hook) => {
		switch(hook.type) {
			case 'AUTO_IMAGE_START':
				setUploading(true);
				break;

			case 'AUTO_IMAGE_END':
				setUploading(false);
				break;
		}
	}

	return (
		<DisabledArea disabled={uploading}>
			<Model model={model} change={change} errors={props.errors != undefined && props?.errors[model.id]}>
				<Input
					name="name"
					direction={(model.rtl != undefined && model.rtl == 'true') ? 'rtl' : null}
					_class={{size:60}}
				>Word or sentence</Input>

				<RichEditorPlaceHolder
					name="description"
					has_emojis={true}
					max_length={50}
					placeholder={i18n("Sort the letters below to get the enigma")}
					disabled={(model.full_image != undefined) ? true : false}
				>Clue</RichEditorPlaceHolder>

				<AutoImageUploader
					auto_url={`/promotions/auto_img/${props.promotion_id}`}
					auto_type='stages'
					changeMulti={props.changeMulti}
					hooks={auto_img_hooks}
					image={model?.full_image ?? ''}
					name="image"
					display_custom_dragging_instructions="Upload the image"
					small_input={true}
					_size={{width:800, height:400}}
				>Clue image</AutoImageUploader>
			</Model>

			{(model.description != "" && model.full_image != undefined) && (
				<Message
					title="Only the image will be displayed"
					_class={{ other:'in_bottom' }}
				>
					<p>{i18n("You can add the text into the image itself or remove the image.")}</p>
				</Message>
			)}
		</DisabledArea>
	)
};

const GameSettings = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promotion = props?.promotion ?? false;
	if(promotion === false) return null;

	const [ errors, setErrors ] = useState({});
	const [ model, setModel ] = useState({});
	const [ create_word, setCreateWord ] = useState(false);
	const [ loading, setLoading ] = useState(false);

	const getMaxAvailTime = (num_words) => {
		if(num_words == undefined) return recommended_time_per_word;
		return num_words * recommended_time_per_word
	};
	const intervaling = useRef(false);
	const max_avail_time = stage.guessword_words != undefined ? getMaxAvailTime(stage.guessword_words.length) : recommended_time_per_word;	// sec

	useEffect(() => {
		if(stage.guessword_words == undefined) validate([]);
	}, []);

	const addWords = (words) => {
		let _words = stage?.guessword_words || [];

		words.forEach((w) => {
			w.id = uuidv4();
			w.type = 'guessword_field';
			_words.push(w);
		});

		change('guessword_words', _words);
		setCreateWord(false);
		validate();
	};

	const onClickOpen = (opened_word) => {
		change('_word_default',opened_word);
	}

	const addWord = () => {
		const current_words = stage?.guessword_words || [];
		change('guessword_words',[...current_words,model]);
		setCreateWord(false);
		validate();
	}

	const trimWords = () => {
		if(!intervaling.current){
			intervaling.current = true;
			setTimeout(()=>{
				let words = stage.guessword_words;
				words.map((w,i) => {
					let wsRegex = /^\s+|\s+$/g;
					w.name = (w.name).replace(wsRegex, "");
					words[i] = w;
				});
				change('guessword_words', words);
				intervaling.current = false;
			},3000);
		}
	}

	const validate = (v) => {
		v = (v == undefined) ? stage.guessword_words : v;
		let errors = {};
		let errors_images = false;

		v.map((w) => {
		
			if(errors[w.id] == undefined) errors[w.id] = {};
			
			if(w.name == undefined || w.name == '') {
				errors[w.id] = i18n("Mandatory word");
				errors_images = true;
			}

			if(w.name != undefined && w.name.length > 0){
				let already_word = v.find((word) => {
					return (word.id != w.id && word.name != undefined && (word.name).toLowerCase() == (w.name).toLowerCase()) ? true : false;
				});
				if(already_word) errors[w.id] =  i18n("Repeated word");
			}
			
			if(Object.keys(errors[w.id]).length == 0) delete errors[w.id];
		});

		if(errors_images) errors['images'] = i18n("The clue is incomplete");

		setErrors(errors);
		change('guessword_words', v);
		setLoading(true);
		setTimeout(() => {
			setLoading(false);
			calculateTotalGameTime();
		}, 0);
		trimWords();
	};

	const calculateTotalGameTime = () => {
		const _words = (stage.guessword_words != undefined) ? (stage.guessword_words).slice(0,total_elements) : [];
		const _min_words = (stage.guessword_min_words != undefined) ? Math.min(_words.length, stage.guessword_min_words) : _words.length;
		const _time_per_word = stage?.guessword_time_per_word ?? 30;
		change('game_time', _time_per_word * _min_words);
	}

	const changeSidebar = (k, v) => {
		change(k, v);
		validate();
	};

	const section_title = `${i18n("Words")} (${((stage.guessword_words != undefined) ? stage.guessword_words.length : 0)}/${total_elements})`;

	const dynamic_fields = [{
		type:'guessword_field',
		label:"Create word",
		translate:[
			{key:'description',type:'input'}
		],
		title:(f) => ((f.name) ? `${f.name}` : ""),
	}];

	const guess_words_for_play = (stage.guessword_words != undefined) ? (stage.guessword_words).slice(0,total_elements) : [];
	
	let guessword_min_words = (stage.guessword_min_words != undefined) ? Math.min(guess_words_for_play.length, stage.guessword_min_words) : guess_words_for_play.length;
	const option_level = [
		{ k:'normal', v:i18n("Easy") },
		{ k:'hard', v:i18n("***Hard") },
	];

	const avail_locales = [];
	if(window.langs != undefined) {
		window.langs.map((loc) => {
			avail_locales.push(loc.I18nPromotion.lang);
		})
	}

	const unsortable_words = stage?.guessword_random_words ?? false;
	const label_tooltip = `<u>${i18n("Easy level")}:</u> ${i18n("When you change one letter, the rest of the letters move, and you can generate more hits in a single move. In the case of sentences, the letters are not mixed between words.")}<br/><u>${i18n("Difficult level")}:</u> ${i18n("When changing a letter, the rest of the letters do not move. In the case of sentences, the letters can be mixed between words.")}`;

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<header class="gui_section_header_builder">
						<h3 class="section_title knk-profile">{i18n("General")}</h3>
					</header>

					<Model model={stage} change={changeSidebar}>
						<Select
							name="guessword_level"
							values={option_level}
							_class={{ size:50 }}
							label_props={{
								tooltip:<Tooltip autotranslate={false} position="right">{label_tooltip}</Tooltip>,
							}}
						>Level</Select>

						{(guess_words_for_play.length > 1) && (
							<Range
								min={1}
								max={guess_words_for_play.length}
								step={1}
								name="guessword_min_words"
								value={guessword_min_words}
							>Minimum number of words to solve</Range>
						)}
						
						{(guess_words_for_play.length > 1) && (
							<Checkbox
								name="guessword_random_words"
							>Show words randomly</Checkbox>
						)}

						{(guess_words_for_play.length > 0) && (
							<Range
								min={10}
								max={90}
								step={10}
								label_props={{
									addition:"In seconds",
									autotranslate:false,
								}}
								name="guessword_time_per_word"
								value={stage?.guessword_time_per_word ?? 30}
							>Maximum time to solve each word</Range>
						)}
					</Model>

					{/*<Message
						title="Idea"
					>
						<p><FormattedMessage id="A good game time is around {time} seconds per word to get the right answer." values={{time:recommended_time_per_word}} /></p>
					</Message>*/}

					{(stage.guessword_level == 'hard') && (
						<Model model={stage} change={changeSidebar}>
							<Checkbox
								name="guessword_skip_time"
							>Enable word skipping</Checkbox>
						</Model>
					)}
				</article>

				<article class="gui_not_in_top_huge">
					<DynamicFields
						section_title={section_title}
						onChange={validate}
						onClickOpen={onClickOpen}
						locales={avail_locales}
						has_translation={true}
						section_icon={false}
						field_types={dynamic_fields}
						sortable={!unsortable_words}
						fields={guess_words_for_play}
						errors={errors}
						custom_add_button={
							<MultiOptionsCreator
								type_ai="palorder"
								promotion={promotion}
								stage_id={stage.id}
								//label_button="Create new words"	// commented to avoid weird line breaking in label
								label_button="Add words"
								label_main="Add words"
								onCreateBulk={() => {
									setCreateWord('upload');
								}}
								onCreateSimple={() => {
									setModel(createField(i18n('Field'), 'guessword_field'));
									setCreateWord('simple');
								}}
								onSuccess={(w) => {
									addWords(w);
									props.saveStage();
								}}
							/>
						}
					>
						<GuessWordField
							promotion_id={stage.promotion_id}
							errors={errors}
							intl={props.intl}
						/>
					</DynamicFields>
				</article>

				{(create_word == 'simple') && (
					<FloatingLayer
						_class={{ size:'normal' }}
						backButton={() => setCreateWord(false)}
					>
						<div>
							<GuessWordField
								model={model}
								change={(k, v) => setModel((state) => {
									return {
										...state,
										[k]:v
									}
								})}
								promotion_id={stage.promotion_id}
								errors={errors}
								intl={props.intl}
							/>

							<ButtonCollection in_floating={true}>
								<ActionButton
									onClick={addWord}
								>Add</ActionButton>

								<SecondaryButton
									onClick={() => setCreateWord(false)}
								>Cancel</SecondaryButton>
							</ButtonCollection>
						</div>
					</FloatingLayer>
				)}

				{(create_word == 'upload') && (
					<FloatingLayer
						_class={{ size:'normal' }}
						backButton={() => setCreateWord(false)}
					>
						<BulkFromFile
							title="Import your words/clues"
							subtitle="You can import up to {max} words/clues in bulk."
							promotion_id={promotion.id}
							type="guess_word"
							limit={total_elements}
							onSuccess={(w) => addWords(w)}
							close={(vs) => setCreateWord(false)}
							fields={[
								{ name:'name', icon:'knk-anagram', label:i18n("Word or sentence") },
								{ name:'description', icon:'knk-search', label:i18n("Clue") },
							]}
							instructions={<><p>{i18n("Create a CSV or text file with two columns, separated by a comma or semicolon. The first column corresponds to the name of the validation point, and the second column the access password.")}</p><p>{i18n("Each line corresponds to a validation point.")}</p></>}
						/>
					</FloatingLayer>
				)}
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promoModel.Custom__Menus} layout_extra_classes="is_new_game">
					<div style={{width:'100%'}}>
						{(loading) ? (
							<div class="gui_not_in_top gui_flex_center" style={{width:'100%',height:'100%'}}>
								<Spinner>Generating a new board</Spinner>
							</div>
						) : (
							(Object.keys(errors).length == 0) ? (
								<GuessWordClient
									stage={{Stage:stage}}
									promotion={props.promotion}
									is_builder={true}
									is_playable={true}
									section="settings"
								/>
							) : (
								<GameContainerWrapper>
									<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
										{Object.values(errors).map((error, i) => {
											let is_object = (typeof error === 'object') ? true : false;
											if(is_object) return null;

											return (
												<p key={`error${i}`}>{error}</p>
											)
										})}
									</div>
								</GameContainerWrapper>
							)
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
}

const GameDesign = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promotion = props?.promotion ?? false;
	if(promotion === false) return null;

	const [ errors, setErrors ] = useState({});
	const [ loading, setLoading ] = useState(false);

	useEffect(() => {
		if(stage.guessword_words == undefined || stage.guessword_words.length == 0) validate([]);
	}, []);

	const validate = (v) => {
		v = (v == undefined) ? stage.guessword_words : v;
		let errors = {};
		let total_words = v.length; //stage.guessword_words != undefined ? Object.keys(stage.guessword_words).length : 0;
		let errors_images = false;

		v.map((w) => {
			if(errors[w.id] == undefined) errors[w.id] = {};

			if(w.name == undefined || w.name == '') {
				errors[w.id] = i18n("Mandatory Word");
				errors_images = true;
			}

			if((w.description == undefined || w.description == '') && (w.image == undefined || w.image == '')) {
				errors[w.id] = i18n("Mandatory Image or Clue");
			}

			if(w.name != undefined && w.name.length > 0){
				let already_word = v.find((word) => {
					return (word.id != w.id && word.name != undefined && (word.name).toLowerCase() == (w.name).toLowerCase()) ? true : false;
				});
				if(already_word) errors[w.id] = i18n("This word already exists");
			}
			if(Object.keys(errors[w.id]).length == 0) delete errors[w.id];
		});
		if(total_words < 1) errors['guessword_words'] = props.intl.formatMessage({ id:"Still missing a word" });
		if(errors_images) errors['images'] = i18n("Some items are missing information");

		setErrors(errors);
		change('guessword_words', v);
		setLoading(true);
		setTimeout(() => {
			setLoading(false);
		}, 0);
	};
	const layout_props = getLayout(promotion);
	const font_families = [
		{ k:'generic', v:`${i18n("***Generic")} (${parseFontName("Arial")})` },
		{ k:'main', v:`${i18n("Main")} (${parseFontName(layout_props.text.main_font)})` },
		{ k:'secondary', v:`${i18n("***Secondary")} (${parseFontName(layout_props.text.secondary_font)})` },
	];

	const font_background = [
		//{ k:null, v:`-- ${i18n("Nothing")} --` },
		{ k:'circle', v:i18n("Circle") },
		{ k:'square', v:i18n("Square") },
		{ k:'image', v:i18n("Custom image") },
	];

	const autouploader_props = {
		auto_url:`/promotions/auto_img/${stage.promotion_id}`,
		auto_type:'stages',
		display_custom_dragging_instructions:"Upload the image",
		small_input:true,
	};

	const promo_main_color = getPromotionMainColor(promotion);

	const is_bg_color_bright = isColorBright(RGB_2_HEX(stage?.bg_color ?? promo_main_color));
	const default_bg_color = stage?.bg_color ?? promo_main_color;

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<header class="gui_section_header_builder">
						<h3 class="section_title knk-profile">{i18n("General")}</h3>
					</header>

					<Model model={stage} change={change}>
						<AutoPostImageUploader
							image={stage.full_guessword_bg_img}
							name='guessword_bg_img'
							_size={{ width:800, height:800 }}
							{...autouploader_props}
						>Background image</AutoPostImageUploader>

						<ColorPicker
							_class={{size:50}}
							_hide_code={true}
							name="bg_color"
							color={default_bg_color}
						>Background color</ColorPicker>

						<Checkbox
							name="display_outter_border"
							value={stage?.display_outter_border ?? true}
						>Display outer border</Checkbox>

						<Select
							name="guessword_font_family"
							values={font_families}
							_class={{size:50}}
						>Typography</Select>

						<Select
							name="guessword_font_bg"
							values={font_background}
							_class={{size:50}}
						>Letters layout</Select>

						<Checkbox
							name="silence"
						>Disable sounds</Checkbox>
					</Model>

					<GameSoundtrackFields
						stage={stage}
						change={change}
					/>
				</article>
					
				<article class="gui_not_in_top_huge">
					<header class="gui_section_header_builder">
						<h3 class="section_title knk-text">{i18n("Initial order of the letters")}</h3>
					</header>

					<Model model={stage} change={change}>
						{(stage.guessword_font_bg == 'image') && (
							<AutoPostImageUploader
								image={stage.full_guessword_letter_bg_img}
								name='guessword_letter_bg_img'
								_size={{ width:100, height:100 }}
								{...autouploader_props}
							>Background image</AutoPostImageUploader>
						)}

						<ColorPicker
							_class={{size:50}}
							_hide_code={true}
							name="guessword_bg_color"
							color={stage?.guessword_bg_color ?? `rgba(${(is_bg_color_bright) ? '0, 0, 0' : '255, 255, 255'}, 0.2`}
						>Background color</ColorPicker>

						<ColorPicker
							_class={{size:50}}
							_hide_code={true}
							name="guessword_font_color"
							color={stage?.guessword_font_color ?? promo_main_color}
						>Text color</ColorPicker>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<header class="gui_section_header_builder">
						<h3 class="section_title knk-on">{i18n("Correct order of the letters")}</h3>
					</header>

					<Model model={stage} change={change}>
						{(stage.guessword_font_bg == 'image') && (
							<AutoPostImageUploader
								image={stage.full_guessword_letter_bg_ok_img}
								name='guessword_letter_bg_ok_img'
								_size={{ width:100, height:100 }}
								{...autouploader_props}
							>Background image</AutoPostImageUploader>
						)}

						<ColorPicker
							_class={{size:50}}
							_hide_code={true}
							name="guessword_bg_color_ok"
							color={stage?.guessword_bg_color_ok ?? ((is_bg_color_bright) ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 1)')}
						>Background color</ColorPicker>

						<ColorPicker
							_class={{size:50}}
							_hide_code={true}
							name="guessword_font_color_ok"
							color={stage?.guessword_font_color_ok ?? promo_main_color}
						>Text color</ColorPicker>
					</Model>
				</article>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promoModel.Custom__Menus} layout_extra_classes="is_new_game">
					<div style={{width:'100%'}}>
						{(loading) ? (
							<div class="gui_not_in_top gui_flex_center" style={{width:'100%',height:'100%'}}>
								<Spinner>Generating a new board</Spinner>
							</div>
						) : (
							(Object.keys(errors).length == 0) ? (
								<GuessWordClient
									stage={{Stage:stage}}
									promotion={props.promotion}
									is_builder={true}
									is_playable={true}
									section="settings"
								/>
							) : (
								<GameContainerWrapper>
									<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
										{Object.values(errors).map((error, i) => {
											let is_object = (typeof error === 'object') ? true : false;
											if(is_object) return null;
											return (
												<p key={`error${i}`}>{error}</p>
											)
										})}
									</div>
								</GameContainerWrapper>
							)
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameTest = ({ stage, change, ...props }) => {
	const restart = () => {
		let new_reset = stage.reset != undefined ? stage.reset + 1 : 0;
		let words = [];
		(stage.guessword_words).map((word)=>{
			if(word.success != undefined)delete word['success'];
			if(word.points != undefined)delete word['points'];
			words.push(word);
		});
		change('reset',new_reset);
		change('guessword_words',words);
	};

	return (
		<>
			<BuilderSidebar>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<ActionButton
					icon='knk-reload'
					onClick={() => restart()}
				>Restart game</ActionButton>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promoModel.Custom__Menus} layout_extra_classes="is_new_game">
					<div style={{width:'100%'}}>
						<GuessWordClient
							stage={{Stage:stage}}
							promotion={props.promotion}
							is_builder={true}
							is_test={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GuessWordBuilder = ({ base_route, stage, ...props }) => {
	const basename = `/stages/${stage.id}/guessword`;

	const disabled_test = ((stage?.guessword_words ?? []).length == 0) ? true :false;

	const nav_sections = [
		{ label:"Go back", link:`/stages/${stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Gameplay settings", link:`${basename}/settings`, icon:'knk-settings' },
		{ label:"Design", link:`${basename}/design`, icon:'knk-artist-mode' },
		{ label:"Instructions", link:`${basename}/instructions`, icon:'knk-help' },
		{ label:"Score screen", link:`${basename}/gamescore`, icon:'knk-like' },
		{ label:"Test the game", link:`${basename}/test`, icon:'knk-ok', disabled:disabled_test },
	];

	const common_props = {
		stage,
		is_multistage:props.is_multistage,
		isCorporate:props.isCorporate,
		change:props.change,
		changeMulti:props.changeMulti,
		intl:props.intl,
		promotion:props.promotion,
		promoModel:props.promoModel,
		saveStage:props.saveStage,
		stage_indicator:props.stage_indicator,
		nav_sections:nav_sections,
	};

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/settings">
						<GameSettings
							{...common_props}
							nav_active={`/settings`}
						/>
					</Route>

					<Route exact={true} path="/design">
						<GameDesign
							{...common_props}
							nav_active={`/design`}
						/>
					</Route>

					<Route exact={true} path="/instructions">
						<GameInstructionsBuilder
							{...common_props}
							nav_active={`/instructions`}
						>
							<GuessWordClient
								stage={{ Stage:stage }}
								promotion={props.promotion}
								is_builder={true}
								section="instructions"
							/>
						</GameInstructionsBuilder>
					</Route>

					<Route exact={true} path="/gamescore">
						<GameScoreBuilder
							{...common_props}
							nav_active={`/gamescore`}
							exclude_cases={['fail', 'out_of_moves']}
						>
							<GuessWordClient
								stage={{ Stage:stage }}
								promotion={props.promotion}
								is_builder={true}
								section="score"
							/>
						</GameScoreBuilder>
					</Route>

					<Route exact={true} path="/test">
						<GameTest
							{...common_props}
							nav_active={`/test`}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
}
export default injectIntl(GuessWordBuilder);